import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Config from '@skin_config';
import NumericInput from '@common/components/numericInput';
import { GetCurrencyConfig } from '@common/helpers/selectors';
import { useTranslation } from '@common/hooks/translation';
import { usePrevious } from '@common/hooks/previous';

import { roundAmount } from '@betslip/src/rules/calculation/helpers';

import { useBalance } from '../../../hooks/balance';

import './style.scss';
import _ from 'lodash';

const suggestedAmountPercents = [10, 25, 50, 75, 100];

const CashoutAmountForm = ({
  errorMessage,
  amount,
  onChange,
  placeholder,
  isMax,
}) => {
  const balance = useBalance();
  const t = useTranslation();
  const maxButton = useRef(null);

  const prevBalance = usePrevious(balance);

  const currencyConfig = useSelector((state) => (
    GetCurrencyConfig(state, Config.main.currencySymbolRounding)
  ));

  useEffect(() => {
    const balanceChanged = !_.isNil(prevBalance) && prevBalance !== balance;
    if (balanceChanged && isMax) onChange(balance, true);
  }, [balance, prevBalance, isMax, onChange]);

  const suggestedAmounts = useMemo(() => {
    const roundCoefficient = Math.max(Math.round(balance).toString().length - 1, 0);
    const roundedBalance = (
      Math.trunc((balance / 10 ** roundCoefficient)) * (10 ** roundCoefficient)
    );

    return (
      [
        ...new Set(
          suggestedAmountPercents.map((percent) => (
            Math.round(roundAmount((roundedBalance * percent) / 100, currencyConfig, false))
          )),
        ),
      ]
    );
  }, [balance, currencyConfig]);

  return (
    <div className="cashout-amount-wrapper">
      <div className="cashout-amount">
        <NumericInput
          value={amount}
          onChange={(value) => onChange(value, false)}
          placeholder={placeholder || 'Cash out amount'}
          maxDecimalCount={currencyConfig?.rounding || 2}
          className={errorMessage ? 'cashout-amount-input-error' : ''}
          style={{ paddingRight: (maxButton.current?.offsetWidth || 0) + 10 }}
        />

        <button ref={maxButton} type="button" onClick={() => onChange(balance, true)}>
          {t('Max')}
        </button>
      </div>

      {errorMessage && <p className="cashout-amount-error-message">{t(errorMessage)}</p>}

      {balance >= 10 && (
        <div className="cashout-amount-suggestions">
          <h3>{t('Suggested for you')}</h3>
          <div>
            {suggestedAmounts.map((suggestion) => (
              <button key={suggestion} type="button" onClick={() => onChange?.(suggestion, false)}>
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

CashoutAmountForm.propTypes = {
  placeholder: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  errorMessage: PropTypes.string,
  isMax: PropTypes.bool,
};

export default CashoutAmountForm;
